import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  Divider,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { adminAPI } from '../../services/api';
import PortalLayout from '../../components/PortalLayout';
import { useToast } from '../../context/ToastContext';

const GESELLSCHAFTSFORMEN = ['GmbH', 'AG', 'UG (haftungsbeschränkt)', 'GmbH & Co. KG', 'KG', 'OHG', 'GbR', 'Einzelunternehmen (e.K.)', 'e.V.', 'Sonstige'];
const ANREDE_OPTIONS = ['Herr', 'Frau', 'Herr Dr.', 'Frau Dr.', 'Herr Prof.', 'Frau Prof.'];

const ContractorApprovalPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [contractors, setContractors] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedContractor, setSelectedContractor] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState<'approve' | 'reject' | 'deactivate' | 'activate'>('approve');
  const [filterStatus, setFilterStatus] = useState('all');

  const [editOpen, setEditOpen] = useState(false);
  const [editUser, setEditUser] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [contractorLeads, setContractorLeads] = useState<any[]>([]);

  const loadContractors = useCallback(async () => {
    try {
      setIsLoading(true);
      const params: any = { role: 'contractor' };
      if (filterStatus !== 'all') params.status = filterStatus;
      const response = await adminAPI.getUsers(params);
      setContractors(response.data?.data || []);
    } catch (err: any) {
      showError('Fehler beim Laden der Dienstleister');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [showError, filterStatus]);

  useEffect(() => {
    loadContractors();
  }, [loadContractors]);

  const handleApproveClick = (contractor: any) => {
    setSelectedContractor(contractor);
    setDialogAction('approve');
    setDialogOpen(true);
  };

  const handleToggleActive = (contractor: any) => {
    setSelectedContractor(contractor);
    setDialogAction(contractor.role === 'deactivated' ? 'activate' : 'deactivate');
    setDialogOpen(true);
  };

  const handleConfirm = async () => {
    try {
      if (dialogAction === 'approve') {
        await adminAPI.updateUserRole(selectedContractor.id, 'contractor');
        showSuccess('Dienstleister genehmigt');
      } else if (dialogAction === 'reject') {
        await adminAPI.deactivateUser(selectedContractor.id);
        showSuccess('Dienstleister abgelehnt');
      } else if (dialogAction === 'deactivate') {
        await adminAPI.deactivateUser(selectedContractor.id);
        showSuccess('Dienstleister deaktiviert');
      } else if (dialogAction === 'activate') {
        await adminAPI.updateUserRole(selectedContractor.id, 'contractor');
        showSuccess('Dienstleister aktiviert');
      }
      setDialogOpen(false);
      loadContractors();
    } catch (err: any) {
      showError('Fehler beim Verarbeiten der Anfrage');
      console.error(err);
    }
  };

  const handleEditClick = async (contractor: any) => {
    setEditUser(contractor);
    setEditForm({
      name: contractor.name || '',
      email: contractor.email || '',
      phone: contractor.phone || '',
      company_name: contractor.company_name || '',
      anrede: contractor.anrede || '',
      gesellschaftsform: contractor.gesellschaftsform || '',
      position: contractor.position || '',
      strasse: contractor.strasse || '',
      plz: contractor.plz || '',
      ort: contractor.ort || '',
      land: contractor.land || 'Deutschland',
      fax: contractor.fax || '',
      mobil: contractor.mobil || '',
      homepage: contractor.homepage || '',
      ust_id: contractor.ust_id || '',
    });
    setContractorLeads([]);
    try {
      const res = await adminAPI.getContractorPurchasedLeads(contractor.id);
      setContractorLeads(res.data.data || []);
    } catch {
    }
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setIsSaving(true);
    try {
      await adminAPI.updateUser(editUser.id, editForm);
      showSuccess('Profil gespeichert');
      setEditOpen(false);
      loadContractors();
    } catch {
      showError('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const getStatusChip = (contractor: any) => {
    if (contractor.role === 'deactivated') {
      return <Chip label="Deaktiviert" color="error" variant="outlined" size="small" />;
    }
    return <Chip label="Aktiv" color="success" variant="outlined" size="small" />;
  };

  if (isLoading) {
    return (
      <PortalLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Call Center Dienstleister Verwaltung
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 3 }}>
          Alle registrierten Call-Center-Dienstleister verwalten und Profile bearbeiten
        </Typography>

        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} label="Status Filter">
              <MenuItem value="all">Alle Dienstleister</MenuItem>
              <MenuItem value="active">Nur Aktive</MenuItem>
              <MenuItem value="inactive">Nur Inaktive / Deaktiviert</MenuItem>
            </Select>
          </FormControl>
        </Paper>

        {contractors.length === 0 ? (
          <Alert severity="info">
            Keine Dienstleister gefunden.
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Unternehmensname</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Telefon</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Registriert am</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">
                    Aktionen
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contractors.map((contractor) => (
                  <TableRow key={contractor.id} hover>
                    <TableCell>{contractor.name}</TableCell>
                    <TableCell>{contractor.email}</TableCell>
                    <TableCell>{contractor.company_name || '-'}</TableCell>
                    <TableCell>{contractor.phone || '-'}</TableCell>
                    <TableCell>{getStatusChip(contractor)}</TableCell>
                    <TableCell>{new Date(contractor.created_at).toLocaleDateString('de-DE')}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => handleEditClick(contractor)}
                        >
                          Bearbeiten
                        </Button>
                        {contractor.role === 'deactivated' ? (
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleApproveClick(contractor)}
                          >
                            Aktivieren
                          </Button>
                        ) : (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleToggleActive(contractor)}
                          >
                            Deaktivieren
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>
            {dialogAction === 'approve' || dialogAction === 'activate' ? 'Dienstleister aktivieren?' : 'Dienstleister deaktivieren?'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {(dialogAction === 'approve' || dialogAction === 'activate')
                ? `Möchten Sie ${selectedContractor?.name} (${selectedContractor?.company_name}) aktivieren?`
                : `Möchten Sie ${selectedContractor?.name} (${selectedContractor?.company_name}) deaktivieren? Der Dienstleister kann sich dann nicht mehr anmelden.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="inherit">Abbrechen</Button>
            <Button
              onClick={handleConfirm}
              color={(dialogAction === 'approve' || dialogAction === 'activate') ? 'success' : 'error'}
              variant="contained"
              autoFocus
            >
              {(dialogAction === 'approve' || dialogAction === 'activate') ? 'Aktivieren' : 'Deaktivieren'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>
            Profil bearbeiten: {editUser?.name}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>Grunddaten</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Anrede</InputLabel>
                  <Select value={editForm.anrede || ''} onChange={(e) => setEditForm({ ...editForm, anrede: e.target.value })} label="Anrede">
                    <MenuItem value="">–</MenuItem>
                    {ANREDE_OPTIONS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField fullWidth size="small" label="Name" value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="E-Mail" type="email" value={editForm.email || ''} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Telefon" value={editForm.phone || ''} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Unternehmensname" value={editForm.company_name || ''} onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Gesellschaftsform</InputLabel>
                  <Select value={editForm.gesellschaftsform || ''} onChange={(e) => setEditForm({ ...editForm, gesellschaftsform: e.target.value })} label="Gesellschaftsform">
                    <MenuItem value="">–</MenuItem>
                    {GESELLSCHAFTSFORMEN.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Position im Unternehmen" value={editForm.position || ''} onChange={(e) => setEditForm({ ...editForm, position: e.target.value })} />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 1 }}>Adresse</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <TextField fullWidth size="small" label="Straße / Nr." value={editForm.strasse || ''} onChange={(e) => setEditForm({ ...editForm, strasse: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="PLZ" value={editForm.plz || ''} onChange={(e) => setEditForm({ ...editForm, plz: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="Ort" value={editForm.ort || ''} onChange={(e) => setEditForm({ ...editForm, ort: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Land</InputLabel>
                  <Select value={editForm.land || 'Deutschland'} onChange={(e) => setEditForm({ ...editForm, land: e.target.value })} label="Land">
                    <MenuItem value="Deutschland">Deutschland</MenuItem>
                    <MenuItem value="Österreich">Österreich</MenuItem>
                    <MenuItem value="Schweiz">Schweiz</MenuItem>
                    <MenuItem value="EU-Ausland">EU-Ausland</MenuItem>
                    <MenuItem value="International">International</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 1 }}>Weitere Kontaktdaten</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Mobil" value={editForm.mobil || ''} onChange={(e) => setEditForm({ ...editForm, mobil: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Fax" value={editForm.fax || ''} onChange={(e) => setEditForm({ ...editForm, fax: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 4 }}>
                <TextField fullWidth size="small" label="Homepage" value={editForm.homepage || ''} onChange={(e) => setEditForm({ ...editForm, homepage: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label="USt-ID (nur EU)" value={editForm.ust_id || ''} onChange={(e) => setEditForm({ ...editForm, ust_id: e.target.value })} />
              </Grid>

              {contractorLeads.length > 0 && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ mt: 2 }}>
                      Gekaufte Leads ({contractorLeads.length})
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f6fa' }}>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Auftrag #</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Firma</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Richtung</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Ort</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Kaufdatum</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem' }}>Preis</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {contractorLeads.map((lead) => (
                          <TableRow key={lead.lead_id}>
                            <TableCell sx={{ fontSize: '0.82rem' }}>#{lead.inquiry_id}</TableCell>
                            <TableCell sx={{ fontSize: '0.82rem' }}>{lead.company_name || lead.contact_name || '–'}</TableCell>
                            <TableCell sx={{ fontSize: '0.82rem' }}>
                              {lead.direction === 'inbound' ? 'Inbound' : lead.direction === 'both' ? 'Beides' : 'Outbound'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.82rem' }}>{lead.city || '–'}</TableCell>
                            <TableCell sx={{ fontSize: '0.82rem' }}>{new Date(lead.purchased_at).toLocaleDateString('de-DE')}</TableCell>
                            <TableCell sx={{ fontSize: '0.82rem' }}>€{parseFloat(lead.purchase_price || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={() => setEditOpen(false)}>Abbrechen</Button>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={isSaving}
              sx={{ background: 'linear-gradient(135deg, #E85D26 0%, #c94d1e 100%)', fontWeight: 700 }}
            >
              {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Speichern'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </PortalLayout>
  );
};

export default ContractorApprovalPage;
